import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../../utils/api';
import { fmtDateTime } from '../../../utils/region';
import OverflowChips, { OChip } from '../../OverflowChips';
import Pagination, { getPageSize } from '../../Pagination';
import Fab from '../../Fab';
import type {
  AwBankAccounts, AwBankAcct, AwBankAccts, AwBankCandidate, AwBankCandidates, AwBankLine, AwBankLinesPage, AwBankReconciliation,
  AwBankStateFilter, AwBankStatement, AwBankStatementPage, AwBankTransferConfirmed, AwBankTransferSuggestion, AwBankTransfers, AwBankUploadResult,
} from '../../../types';
import type { AwCtx } from '../ctx';
import { amt, btn, btnPrimary, card, dmy, errBox, inp, note, num, openBlob, pill, pillAmber, pillGreen, pillGrey, sectionHead, table, td, th } from '../ui';
import { Dialog, Loader, Stat, actBtn, cancelBtn, chip, chipOn, emptyLine, field, lbl, r2, todayIso } from '../dialog';

// ── Banking › Bank accounts (owner 2026-10-13) ──────────────────────────────────────────────────
// Three different things that all answer "which bank accounts does this company have", kept apart
// because they are different records: the accounts the company prints on its own invoices (its
// portal profile), the single account on the firm's client record, and the account numbers that have
// a statutory bank book. The firm's own bank_transactions are the FIRM's money and are not here.
export function BankAccountsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [d, setD] = useState<AwBankAccounts | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awBankAccounts(ctx.clientId).then((x: AwBankAccounts) => { if (alive) setD(x); }).catch((e: any) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  if (err) return <div style={errBox}>{err}</div>;
  if (!d) return <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;
  const empty = <div style={note}>{t('aw_bank_none')}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={sectionHead}>{t('aw_bank_merchant')}</div>
        <div style={card}>
          {!d.merchant?.length ? empty : (
            <table style={table}>
              <thead><tr><th style={th}>{t('aw_bank_type')}</th><th style={th}>{t('aw_bank_bankname')}</th><th style={th}>{t('aw_bank_name')}</th><th style={th}>{t('aw_bank_number')}</th><th style={th}>{t('aw_bank_swift')}</th><th style={th}>{t('aw_col_currency')}</th></tr></thead>
              <tbody>{d.merchant.map((a, i) => (
                <tr key={i}><td style={td}>{a.account_type}</td><td style={td}>{a.bank_name}</td><td style={td}>{a.account_name}</td><td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{a.account_number}</td><td style={td}>{a.swift_code}</td><td style={td}>{a.currency}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
      <div>
        <div style={sectionHead}>{t('aw_bank_client')}</div>
        <div style={card}>
          {!d.client || !(d.client.bank_account_no || d.client.bank_name) ? empty : (
            <table style={table}>
              <thead><tr><th style={th}>{t('aw_bank_bankname')}</th><th style={th}>{t('aw_bank_branch')}</th><th style={th}>{t('aw_bank_name')}</th><th style={th}>{t('aw_bank_number')}</th></tr></thead>
              <tbody><tr><td style={td}>{d.client.bank_name}</td><td style={td}>{d.client.bank_branch}</td><td style={td}>{d.client.bank_account_name}</td><td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{d.client.bank_account_no}</td></tr></tbody>
            </table>
          )}
        </div>
      </div>
      <div>
        <div style={sectionHead}>{t('aw_bank_books')}</div>
        <div style={card}>
          {!d.books?.length ? empty : (
            <table style={table}>
              <thead><tr><th style={th}>{t('aw_bank_name')}</th><th style={th}>{t('aw_bank_number')}</th><th style={th}>{t('aw_col_currency')}</th></tr></thead>
              <tbody>{d.books.map(b => (
                <tr key={b.id}><td style={td}>{b.name}</td><td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{b.account_number}</td><td style={td}>{b.currency}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Banking › Transactions · Reconciliation · Transfers (owner 2026-10-13, batch 2) ─────────────
// The CLIENT COMPANY's own bank statements, read from merchant_bank_statements / merchant_bank_lines
// through /api/acct-workspace/bank/* — the firm's bank_transactions are never touched or shown.
// Matching is a suggestion the accountant confirms, and confirming only MARKS the statement line
// (owner's announced rule): no journal entry, no receipt, no change to the document's paid figure or
// status. A PDF password is typed for one upload, sent with that request alone, and kept nowhere —
// not in state after the answer, not in the URL, never echoed by the server.

const STATES: AwBankStateFilter[] = ['all', 'unmatched', 'matched', 'ignored', 'transfer'];
// 'payment' is what a line carries once a supplier bill was paid from it (services/merchantPurchases.ts
// recordPayment writes match_kind 'payment' — the candidate was offered as 'payable'); without its own
// label the pill printed the raw token in every language (finance review 2026-10-13). 'reimbursement'
// is batch 4's register: offered as a candidate for a money-out line (approved, unpaid, equal total) and
// carried as match_kind once paid from it — the same token both ways, so one label serves both.
const KIND_KEY: Record<string, string> = {
  invoice: 'aw_bank_kind_invoice', quotation: 'aw_bank_kind_quotation', receipt: 'aw_bank_kind_receipt',
  payable: 'aw_bank_kind_payable', payment: 'aw_bank_kind_payment', reimbursement: 'aw_bank_kind_reimbursement', transfer: 'aw_bank_kind_transfer',
};
// Transfers wear blue so the three marked states read apart at a glance: green = matched to a
// document, blue = the company's own money moving between its accounts, amber = set aside on purpose.
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
// The filter chips, field styles, loader, stat card and Dialog live in ../dialog (shared with Purchases).
type TF = (key: string, opts?: Record<string, unknown>) => string;
const acctLabel = (a: AwBankAcct) => [a.bank_name, a.account_number].filter(Boolean).join(' ') || a.account_name || a.id;
/** The statement's opening row is the one line with neither debit nor credit (the ingest's opening-row
 *  rule): it is a balance, not a movement, so it is never matched, ignored or paired as a transfer. */
const isOpening = (l: AwBankLine) => !Number(l.debit) && !Number(l.credit);
const lineAmount = (l: AwBankLine) => r2(Number(l.credit) || Number(l.debit) || 0);
const kindLabel = (t: TF, kind: string) => KIND_KEY[kind] ? t(KIND_KEY[kind]) : kind;
/** The route's error codes (SPEC-B2 §3) turned into a sentence. The two facts a refusal can carry —
 *  the account number printed in the file and the one chosen — are named through i18n labels; the
 *  server's own English `message` is not repeated after the translated sentence. */
const readErr = (e: any, t: TF): string => {
  const code = String(e?.message || '');
  const p = e?.payload && typeof e.payload === 'object' ? e.payload : {};
  const facts = [
    p.accountNumber ? `${t('aw_bank_err_file_account')}: ${p.accountNumber}` : '',
    p.expectedAccountNumber ? `${t('aw_bank_err_chosen_account')}: ${p.expectedAccountNumber}` : '',
  ].filter(Boolean).join(' · ');
  const tail = facts ? ` (${facts})` : '';
  if (code === 'account_not_registered') return t('aw_bank_err_not_registered') + tail;
  if (code === 'statement_account_mismatch') return t('aw_bank_err_mismatch') + tail;
  if (/has matched lines/i.test(code)) return t('aw_bank_err_has_matched');
  if (e?.status === 409) return `${t('aw_bank_err_conflict')}${code ? ` — ${code}` : ''}`;
  return code || t('aw_bank_err_generic');
};

function MatchPill({ l }: { l: AwBankLine }) {
  const { t } = useTranslation();
  if (isOpening(l)) return <span style={pillGrey}>{t('aw_bank_opening')}</span>;
  const who = l.matched_by_name ? `${l.matched_by_name}${l.matched_at ? ` · ${fmtDateTime(l.matched_at)}` : ''}` : '';
  switch (l.match_state) {
    case 'matched':
      return <span style={pillGreen} title={[who, l.match_note].filter(Boolean).join(' — ') || undefined}>{kindLabel(t, l.match_kind)} · {l.match_doc_no} · {amt(l.match_amount)}</span>;
    case 'transfer':
      return <span style={pillBlue} title={who || undefined}>{t('aw_bank_kind_transfer')}</span>;
    case 'ignored':
      // The reason is on hover — the row stays compact, the decision stays explained.
      return <span style={pillAmber} title={[l.match_note, who].filter(Boolean).join(' — ') || undefined}>{t('aw_bank_state_ignored')}</span>;
    default:
      return <span style={pillGrey}>{t('aw_bank_state_unmatched')}</span>;
  }
}

// ── Upload dialog ───────────────────────────────────────────────────────────────────────────────
function UploadDialog({ ctx, accounts, defaultAccountId, onClose, onUploaded }: {
  ctx: AwCtx; accounts: AwBankAcct[]; defaultAccountId: string; onClose: () => void; onUploaded: () => void;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [accountId, setAccountId] = useState(defaultAccountId);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<AwBankUploadResult | null>(null);
  const [inputKey, setInputKey] = useState(0);   // remounts the file input so the same file can be chosen again
  const submit = async () => {
    if (!file || busy) return;
    setBusy(true); setErr(''); setResult(null);
    try {
      const r: AwBankUploadResult = await api.awBankStatementUpload(ctx.clientId, file, { accountId: accountId || undefined, password: password || undefined });
      setResult(r); setFile(null); setInputKey(k => k + 1); onUploaded();
    } catch (e: any) { setErr(readErr(e, t)); }
    // Whatever the outcome, the typed password is dropped the moment the request is answered.
    finally { setPassword(''); setBusy(false); }
  };
  return (
    <Dialog title={t('aw_bank_upload')} onClose={onClose} busy={busy}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={lbl}>{t('aw_bank_file')}</label>
          <input key={inputKey} type="file" accept=".pdf,.csv,application/pdf,text/csv" onChange={e => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12.5 }} />
          <div style={note}>{t('aw_bank_file_hint')}</div>
        </div>
        <div>
          <label style={lbl}>{t('aw_bank_account')}</label>
          <select value={accountId} onChange={e => setAccountId(e.target.value)} style={field}>
            <option value="">{t('aw_bank_auto_detect')}</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{acctLabel(a)}{a.currency ? ` · ${a.currency}` : ''}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>{t('aw_bank_pw')}</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" style={field} />
          <div style={note}><i className="ti ti-lock" aria-hidden /> {t('aw_bank_pw_hint')}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
        {result && (
          <div style={{ ...card, background: 'var(--accent-bg)', borderColor: 'var(--accent)', fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700 }}>{t('aw_bank_upload_done', { added: result.added, skipped: result.skipped, rows: result.rows })}</div>
            <div>{result.accountNumber}{result.periodFrom || result.periodTo ? ` · ${dmy(result.periodFrom)} – ${dmy(result.periodTo)}` : ''}{result.closing !== null && result.closing !== undefined ? ` · ${t('aw_bank_closing')}: ${amt(result.closing)}` : ''}</div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={busy} style={cancelBtn}>{result ? t('aw_bank_close') : t('aw_bank_cancel')}</button>
          <button onClick={submit} disabled={!file || busy} style={{ ...btnPrimary, opacity: !file || busy ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-upload'} aria-hidden /> {t('aw_bank_upload_btn')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Manual line dialog (the Fab) ────────────────────────────────────────────────────────────────
function ManualLineDialog({ ctx, accounts, defaultAccountId, onClose, onSaved }: {
  ctx: AwCtx; accounts: AwBankAcct[]; defaultAccountId: string; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [accountId, setAccountId] = useState(defaultAccountId || accounts[0]?.id || '');
  const [date, setDate] = useState(todayIso());
  const [time, setTime] = useState('');
  const [description, setDescription] = useState('');
  const [dir, setDir] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState('');
  const [channel, setChannel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const n = Number(amount);
  const ready = !!accountId && /^\d{4}-\d{2}-\d{2}$/.test(date) && !!description.trim() && Number.isFinite(n) && n > 0;
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await api.awBankLineAdd(ctx.clientId, {
        accountId, txn_date: date, txn_time: time || undefined, description: description.trim(),
        debit: dir === 'debit' ? r2(n) : 0, credit: dir === 'credit' ? r2(n) : 0,
        balance: balance.trim() === '' ? undefined : Number(balance), channel: channel.trim() || undefined,
      });
      onSaved();
    } catch (e: any) { setErr(readErr(e, t)); setBusy(false); }
  };
  const seg = (on: boolean): React.CSSProperties => ({ ...btn, flex: 1, justifyContent: 'center', ...(on ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) });
  return (
    <Dialog title={t('aw_bank_add_line')} onClose={onClose} busy={busy}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={note}>{t('aw_bank_manual_hint')}</div>
        <div>
          <label style={lbl}>{t('aw_bank_account')}</label>
          <select value={accountId} onChange={e => setAccountId(e.target.value)} style={field}>
            {accounts.map(a => <option key={a.id} value={a.id}>{acctLabel(a)}{a.currency ? ` · ${a.currency}` : ''}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={lbl}>{t('aw_col_date')}</label><input type="date" value={date} onChange={e => setDate(e.target.value)} style={field} /></div>
          <div><label style={lbl}>{t('aw_bank_time')}</label><input type="time" value={time} onChange={e => setTime(e.target.value)} style={field} /></div>
        </div>
        <div><label style={lbl}>{t('aw_bank_description')}</label><input value={description} onChange={e => setDescription(e.target.value)} style={field} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={lbl}>{t('aw_bank_direction')}</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setDir('credit')} style={seg(dir === 'credit')}>{t('aw_bank_credit')}</button>
              <button type="button" onClick={() => setDir('debit')} style={seg(dir === 'debit')}>{t('aw_bank_debit')}</button>
            </div>
          </div>
          <div><label style={lbl}>{t('aw_bank_amount')}</label><input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} style={field} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={lbl}>{t('aw_bank_balance_opt')}</label><input type="number" step="0.01" value={balance} onChange={e => setBalance(e.target.value)} style={field} /></div>
          <div><label style={lbl}>{t('aw_bank_channel_opt')}</label><input value={channel} onChange={e => setChannel(e.target.value)} style={field} /></div>
        </div>
        {err && <div style={errBox}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={busy} style={cancelBtn}>{t('aw_bank_cancel')}</button>
          <button onClick={submit} disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-plus'} aria-hidden /> {t('aw_bank_save')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Match dialog ────────────────────────────────────────────────────────────────────────────────
function MatchDialog({ ctx, line, onClose, onDone }: { ctx: AwCtx; line: AwBankLine; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AwBankCandidates | null>(null);
  const [err, setErr] = useState('');
  const [pick, setPick] = useState<AwBankCandidate | null>(null);
  const full = lineAmount(line);
  const [amount, setAmount] = useState(String(full));
  const [noteTxt, setNoteTxt] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.awBankCandidates(ctx.clientId, line.id).then((d: AwBankCandidates) => { if (alive) setData(d); }).catch((e: any) => { if (alive) { setData({ line, candidates: [] }); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, line.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  const exact = (c: AwBankCandidate) => r2(Number(c.amount) || 0) === full;
  // Server order is score DESC then date DESC; exact-amount rows are lifted to the top (stable sort keeps the rest as served).
  const sorted = useMemo(() => [...(data?.candidates || [])].sort((a, b) => Number(exact(b)) - Number(exact(a))), [data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const n = Number(amount);
  // A reimbursement is paid whole (SPEC-B4 §2: the line must equal the claim's total, 400 otherwise), so
  // once one is picked the amount is pinned to the line and the field is locked — the server's refusal
  // is never sent. Every other kind keeps the part-amount door open.
  const fullOnly = pick?.kind === 'reimbursement';
  const ready = !!pick && Number.isFinite(n) && n > 0 && r2(n) <= full && (!fullOnly || r2(n) === full);
  // Picking a document leaves the amount at the LINE amount (SPEC-B2 §5): a line settling several
  // papers is marked for the one chosen by typing the part that belongs to it — the field is right there.
  const choose = (c: AwBankCandidate) => { setPick(c); setAmount(String(full)); };
  const submit = async () => {
    if (!ready || !pick || busy) return;
    setBusy(true); setErr('');
    try { await api.awBankMatch(ctx.clientId, line.id, { kind: pick.kind, docId: pick.id, amount: r2(n), note: noteTxt.trim() || undefined }); onDone(); }
    catch (e: any) { setErr(readErr(e, t)); setBusy(false); }
  };
  const isCredit = Number(line.credit) > 0;
  return (
    <Dialog title={t('aw_bank_match_title')} onClose={onClose} busy={busy} width={720}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5 }}>
          <span style={{ whiteSpace: 'nowrap' }}>{dmy(line.txn_date)}{line.txn_time ? ` ${line.txn_time}` : ''}</span>
          <span style={{ fontWeight: 700, flex: '1 1 200px' }}>{line.description}{line.details ? <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}> · {line.details}</span> : null}</span>
          <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{isCredit ? t('aw_bank_credit') : t('aw_bank_debit')} {amt(full)}</span>
        </div>
        <div style={note}>{t('aw_bank_marks_only_note')}</div>
        <div style={{ ...card, padding: 0, overflowX: 'auto', maxHeight: 320, overflowY: 'auto' }}>
          {data === null ? <Loader /> : !sorted.length ? <div style={emptyLine}>{t('aw_bank_no_candidates')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th} />
                  <th style={th}>{t('aw_col_type')}</th>
                  <th style={th}>{t('aw_col_no')}</th>
                  <th style={th}>{t('aw_bank_cand_name')}</th>
                  <th style={th}>{t('aw_col_date')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_cand_amount')}</th>
                  <th style={th}>{t('aw_bank_cand_reason')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(c => {
                  const on = pick?.id === c.id && pick?.kind === c.kind;
                  return (
                    <tr key={`${c.kind}:${c.id}`} onClick={() => choose(c)} style={{ cursor: 'pointer', background: on ? 'var(--accent-bg)' : undefined }}>
                      <td style={td}><input type="radio" checked={on} onChange={() => choose(c)} aria-label={c.doc_no} /></td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{kindLabel(t, c.kind)}</td>
                      <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{c.doc_no}</td>
                      <td style={td}>{c.name}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(c.date)}</td>
                      <td style={num}>{amt(c.amount)}{c.currency ? ` ${c.currency}` : ''} {exact(c) && <span style={{ ...pillGreen, marginLeft: 6 }}>{t('aw_bank_exact')}</span>}</td>
                      <td style={{ ...td, fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 10 }}>
          <div>
            <label style={lbl}>{t('aw_bank_amount')}</label>
            <input type="number" step="0.01" min="0" max={full} value={amount} onChange={e => setAmount(e.target.value)} disabled={fullOnly} style={{ ...field, opacity: fullOnly ? 0.7 : 1 }} />
            {Number.isFinite(n) && n > full && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{t('aw_bank_amount_over', { max: amt(full) })}</div>}
            {fullOnly && <div style={{ ...note, marginTop: 3 }}>{t('aw_rb_match_full_only')}</div>}
          </div>
          <div><label style={lbl}>{t('aw_bank_note_opt')}</label><input value={noteTxt} onChange={e => setNoteTxt(e.target.value)} style={field} /></div>
        </div>
        {err && <div style={errBox}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={busy} style={cancelBtn}>{t('aw_bank_cancel')}</button>
          <button onClick={submit} disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-link'} aria-hidden /> {t('aw_bank_confirm_match')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Ignore dialog ───────────────────────────────────────────────────────────────────────────────
function IgnoreDialog({ ctx, line, onClose, onDone }: { ctx: AwCtx; line: AwBankLine; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ready = !!reason.trim();
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { await api.awBankIgnore(ctx.clientId, line.id, reason.trim()); onDone(); }
    catch (e: any) { setErr(readErr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={t('aw_bank_ignore_title')} onClose={onClose} busy={busy} width={460}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{dmy(line.txn_date)} · <b style={{ color: 'var(--text-primary)' }}>{line.description}</b> · {amt(lineAmount(line))}</div>
        <div>
          <label style={lbl}>{t('aw_bank_ignore_reason')}</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} style={{ ...field, resize: 'vertical' }} />
          <div style={note}>{t('aw_bank_ignore_hint')}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={busy} style={cancelBtn}>{t('aw_bank_cancel')}</button>
          <button onClick={submit} disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.5 : 1 }}>
            <i className={busy ? 'ti ti-loader' : 'ti ti-eye-off'} aria-hidden /> {t('aw_bank_ignore_btn')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Statements list (collapsible under the toolbar) ─────────────────────────────────────────────
function StatementsList({ ctx, accountId, onChanged }: { ctx: AwCtx; accountId: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AwBankStatementPage | null>(null);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let alive = true;
    api.awBankStatements(ctx.clientId, { accountId, page, limit: 100 })
      .then((d: AwBankStatementPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit: 100 }); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, accountId, page]);   // eslint-disable-line react-hooks/exhaustive-deps
  const open = async (s: AwBankStatement) => {
    if (busy) return; setBusy(s.id); setErr('');
    try { openBlob(await api.awBankStatementFileBlobUrl(ctx.clientId, s.id)); } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(''); }
  };
  const del = async (s: AwBankStatement) => {
    if (busy || !window.confirm(t('aw_bank_stmt_delete_confirm', { name: s.filename }))) return;
    setBusy(s.id); setErr('');
    try { await api.awBankStatementDelete(ctx.clientId, s.id); onChanged(); } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(''); }
  };
  const rows = data?.rows || [];
  return (
    <div>
      <div style={sectionHead}>{t('aw_bank_statements')}</div>
      {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_bank_no_statements')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_bank_file')}</th><th style={th}>{t('aw_bank_account')}</th><th style={th}>{t('aw_col_period')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_rows')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_added')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_closing')}</th>
                <th style={th}>{t('aw_bank_uploaded_by')}</th><th style={th}>{t('aw_exp_uploaded')}</th><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id}>
                  <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.filename}>
                    <i className={s.source === 'csv' ? 'ti ti-file-type-csv' : 'ti ti-file-type-pdf'} style={{ color: 'var(--text-tertiary)' }} aria-hidden /> {s.filename}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{s.account_number}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{s.period_from || s.period_to ? `${dmy(s.period_from)} – ${dmy(s.period_to)}` : ''}</td>
                  <td style={num}>{s.row_count}</td>
                  <td style={num}>{s.added_count}</td>
                  <td style={num}>{s.closing_balance === null || s.closing_balance === undefined ? '' : amt(s.closing_balance)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{s.created_by_name} <span style={pillGrey}>{s.uploaded_via === 'portal' ? t('aw_bank_via_portal') : t('aw_bank_via_workspace')}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDateTime(s.created_at)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => open(s)} disabled={!!busy} style={{ ...actBtn, marginRight: 6 }}><i className={busy === s.id ? 'ti ti-loader' : 'ti ti-file-search'} aria-hidden /> {t('aw_bank_open')}</button>
                    <button onClick={() => del(s)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }} title={t('aw_bank_stmt_delete_hint')}><i className="ti ti-trash" aria-hidden /> {t('aw_bank_delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || 100} onPage={setPage} />}
    </div>
  );
}

// ── Transactions pane ───────────────────────────────────────────────────────────────────────────
export function TransactionsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  // Reconciliation's "Work the unmatched" — and a colleague's pasted link — hand over ?state=&accountId=.
  // They are read when the pane mounts, then taken off the URL: a filter the accountant changes
  // afterwards must not be undone by the next remount (the shell remounts panes on every period
  // change). The strip waits until the period list has answered: on a deep link the shell may mount
  // this pane before /ledger/periods resolves and remount it right after, and stripping on that first
  // mount would hand the second one an empty URL (reviewer 2026-10-13).
  const [accountId, setAccountId] = useState(() => params.get('accountId') || '');
  const [state, setState] = useState<AwBankStateFilter>(() => { const s = params.get('state') || ''; return (STATES as string[]).includes(s) ? s as AwBankStateFilter : 'all'; });
  useEffect(() => {
    if (ctx.periodsLoading) return;
    if (!params.has('state') && !params.has('accountId')) return;
    setParams(p => { const n = new URLSearchParams(p); n.delete('state'); n.delete('accountId'); return n; }, { replace: true });
  }, [ctx.periodsLoading, params, setParams]);

  const [accounts, setAccounts] = useState<AwBankAcct[] | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwBankLinesPage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [showStatements, setShowStatements] = useState(false);
  const [dlg, setDlg] = useState<{ kind: 'upload' } | { kind: 'manual' } | { kind: 'match'; line: AwBankLine } | { kind: 'ignore'; line: AwBankLine } | null>(null);
  // Every write here goes through ctx.bump, the workspace's one re-read signal (ctx.ts): this pane's
  // own lists and badges re-read on ctx.reload like every sibling pane, and any pane mounted later sees
  // the change too — no pane-local counter beside it (reviewer 2026-10-13).
  const refresh = ctx.bump;

  useEffect(() => {
    let alive = true;
    api.awBankAccts(ctx.clientId).then((d: AwBankAccts) => { if (alive) setAccounts(d.rows || []); }).catch((e: any) => { if (alive) { setAccounts([]); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.awBankLines(ctx.clientId, { accountId, from, to, state, q: qd, page, limit })
      .then((d: AwBankLinesPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, accountId, from, to, state, qd, page, limit]);   // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(id); setErr('');
    try { await fn(); refresh(); } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(''); }
  };
  const unmatch = (l: AwBankLine) => act(l.id, () => api.awBankUnmatch(ctx.clientId, l.id));
  const delLine = (l: AwBankLine) => { if (window.confirm(t('aw_bank_line_delete_confirm'))) act(l.id, () => api.awBankLineDelete(ctx.clientId, l.id)); };

  const accountChips: OChip[] = [
    { key: '', label: t('aw_bank_all_accounts'), active: accountId === '', onClick: () => { setAccountId(''); setPage(1); } },
    // The unmatched count rides in the label — OverflowChips folds labels, not badges, so a folded
    // account still says how much work it holds.
    ...(accounts || []).map(a => ({
      key: a.id, label: a.unmatched ? `${acctLabel(a)} · ${a.unmatched}` : acctLabel(a), title: a.account_name || undefined,
      active: a.id === accountId, onClick: () => { setAccountId(a.id); setPage(1); },
    })),
  ];
  const stateChips: OChip[] = STATES.map(s => ({ key: s, label: t(`aw_bank_state_${s}`), active: s === state, onClick: () => { setState(s); setPage(1); } }));

  const rows = data?.rows || [];
  const sum = accountId ? data?.summary : undefined;
  const noAccounts = accounts !== null && !accounts.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* row 1: account chips · state chips — one row each half, folding into More when short of room */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <OverflowChips chips={accountChips} chipStyle={chip} activeStyle={chipOn} style={{ flex: '3 1 0' }} />
        <div style={{ width: 1, height: 22, background: 'var(--border-strong)', flexShrink: 0 }} aria-hidden />
        <OverflowChips chips={stateChips} chipStyle={chip} activeStyle={chipOn} style={{ flex: '2 1 0' }} />
      </div>
      {/* row 2: date range · search · actions (the search box sits on the second row, the lists' house rule) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} title={t('aw_bank_from')} style={inp} />
        <span style={{ color: 'var(--text-tertiary)' }}>–</span>
        <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} title={t('aw_bank_to')} style={inp} />
        {(from || to) && (
          <button onClick={() => { setFrom(''); setTo(''); setPage(1); }} title={t('aw_bank_clear_dates')} style={{ ...actBtn, padding: '4px 7px' }}><i className="ti ti-x" aria-hidden /></button>
        )}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_bank_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setDlg({ kind: 'upload' })} disabled={noAccounts} title={noAccounts ? t('aw_bank_no_accounts_line') : undefined} style={{ ...btnPrimary, opacity: noAccounts ? 0.5 : 1 }}>
          <i className="ti ti-upload" aria-hidden /> {t('aw_bank_upload')}
        </button>
        <button onClick={() => setShowStatements(v => !v)} aria-pressed={showStatements} style={{ ...btn, ...(showStatements ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) }}>
          <i className="ti ti-files" aria-hidden /> {t('aw_bank_statements')}
        </button>
      </div>

      {err && <div style={errBox}>{err}</div>}
      {showStatements && <StatementsList ctx={ctx} accountId={accountId} onChanged={refresh} />}

      {/* One account = one currency, so its totals may be summed; across accounts the server sends none and none is made up here. */}
      {sum && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Stat label={t('aw_bank_sum_credits')} value={amt(sum.credits)} />
          <Stat label={t('aw_bank_sum_debits')} value={amt(sum.debits)} />
          <Stat label={t('aw_bank_sum_unmatched_credits')} value={amt(sum.unmatchedCredits)} tone={Number(sum.unmatchedCredits) ? '#92400E' : undefined} />
          <Stat label={t('aw_bank_sum_unmatched_debits')} value={amt(sum.unmatchedDebits)} tone={Number(sum.unmatchedDebits) ? '#92400E' : undefined} />
          <Stat label={t('aw_bank_sum_count')} value={sum.count} />
        </div>
      )}

      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null || accounts === null ? <Loader /> : !rows.length ? (
          <div style={emptyLine}>{noAccounts ? t('aw_bank_no_accounts_line') : t('aw_bank_no_lines')}</div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_col_date')}</th>
                {!accountId && <th style={th}>{t('aw_bank_account')}</th>}
                <th style={th}>{t('aw_bank_description')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_debit')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_credit')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_balance')}</th>
                <th style={th}>{t('aw_bank_match')}</th>
                <th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(l => {
                const opening = isOpening(l);
                const tone = opening ? 'var(--text-tertiary)' : undefined;
                return (
                  <tr key={l.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: tone }}>
                      {dmy(l.txn_date)}
                      {l.txn_time && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{l.txn_time}</div>}
                    </td>
                    {!accountId && <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: tone }}>{l.account_number}</td>}
                    <td style={{ ...td, color: tone }}>
                      <div style={{ fontWeight: opening ? 400 : 600 }}>{l.description}</div>
                      {l.details && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{l.details}</div>}
                      {(l.channel || l.source === 'manual') && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                          {l.channel && <span style={pillGrey}>{l.channel}</span>}
                          {l.source === 'manual' && <span style={pillGrey} title={l.created_by_name || undefined}>{t('aw_bank_manual')}</span>}
                        </div>
                      )}
                    </td>
                    <td style={{ ...num, color: tone }}>{Number(l.debit) ? amt(l.debit) : ''}</td>
                    <td style={{ ...num, color: tone }}>{Number(l.credit) ? amt(l.credit) : ''}</td>
                    <td style={{ ...num, color: tone }}>{l.balance === null || l.balance === undefined ? '' : amt(l.balance)}</td>
                    <td style={td}><MatchPill l={l} /></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {!opening && l.match_state === 'none' && (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button onClick={() => setDlg({ kind: 'match', line: l })} disabled={!!busy} style={actBtn}><i className="ti ti-link" aria-hidden /> {t('aw_bank_match_btn')}</button>
                          <button onClick={() => setDlg({ kind: 'ignore', line: l })} disabled={!!busy} style={actBtn}><i className="ti ti-eye-off" aria-hidden /> {t('aw_bank_ignore_btn')}</button>
                          {l.source === 'manual' && <button onClick={() => delLine(l)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}><i className={busy === l.id ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_bank_delete')}</button>}
                        </span>
                      )}
                      {!opening && l.match_state !== 'none' && (
                        <button onClick={() => unmatch(l)} disabled={!!busy} style={actBtn} title={l.match_state === 'transfer' ? t('aw_bank_unmatch_transfer_hint') : undefined}>
                          <i className={busy === l.id ? 'ti ti-loader' : 'ti ti-link-off'} aria-hidden /> {t('aw_bank_unmatch_btn')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* Pager driven by the SERVER's echoed limit, the workspace convention (Documents.tsx). */}
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_bank_marks_only_note')}</div>

      {accounts && accounts.length > 0 && <Fab onClick={() => setDlg({ kind: 'manual' })} title={t('aw_bank_add_line')} />}
      {dlg?.kind === 'upload' && <UploadDialog ctx={ctx} accounts={accounts || []} defaultAccountId={accountId} onClose={() => setDlg(null)} onUploaded={refresh} />}
      {dlg?.kind === 'manual' && <ManualLineDialog ctx={ctx} accounts={accounts || []} defaultAccountId={accountId} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {dlg?.kind === 'match' && <MatchDialog ctx={ctx} line={dlg.line} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.kind === 'ignore' && <IgnoreDialog ctx={ctx} line={dlg.line} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
    </div>
  );
}

// ── Reconciliation pane ─────────────────────────────────────────────────────────────────────────
// One account at a time, in its own currency. Counts and totals are the account's statement lines
// by match state; the latest statement's closing balance is printed as the bank stated it. No ledger
// comparison here — that would be a new figure, and this batch invents none.
export function ReconciliationPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState<AwBankAcct[] | null>(null);
  const [accountId, setAccountId] = useState('');
  const [data, setData] = useState<AwBankReconciliation | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awBankAccts(ctx.clientId).then((d: AwBankAccts) => {
      if (!alive) return;
      const rows: AwBankAcct[] = d.rows || [];
      setAccounts(rows);
      setAccountId(cur => (cur && rows.some(a => a.id === cur)) ? cur : (rows[0]?.id || ''));
    }).catch((e: any) => { if (alive) { setAccounts([]); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    setData(null);
    api.awBankReconciliation(ctx.clientId, accountId).then((d: AwBankReconciliation) => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) setErr(readErr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, accountId]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Same page, Transactions submenu, pre-filtered: the pane reads ?state=&accountId= on mount.
  const workUnmatched = () => setParams(p => {
    const n = new URLSearchParams(p); n.set('menu', 'banking'); n.set('sub', 'transactions'); n.set('state', 'unmatched'); n.set('accountId', accountId); return n;
  }, { replace: true });

  if (accounts === null) return <Loader />;
  if (!accounts.length) return <div style={emptyLine}>{t('aw_bank_no_accounts_line')}</div>;
  const acct = data?.account || accounts.find(a => a.id === accountId) || null;
  const cur = acct?.currency ? ` (${acct.currency})` : '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('aw_bank_account')}</label>
        <select value={accountId} onChange={e => setAccountId(e.target.value)} style={{ ...inp, minWidth: 220 }}>
          {accounts.map(a => <option key={a.id} value={a.id}>{acctLabel(a)}{a.currency ? ` · ${a.currency}` : ''}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={workUnmatched} disabled={!data || !data.counts.unmatched} style={{ ...btnPrimary, opacity: !data || !data.counts.unmatched ? 0.5 : 1 }}>
          <i className="ti ti-list-check" aria-hidden /> {t('aw_bank_rc_work')}
        </button>
      </div>
      {err && <div style={errBox}>{err}</div>}
      {!data ? <Loader /> : (
        <>
          <div>
            <div style={sectionHead}>{t('aw_bank_rc_counts')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Stat label={t('aw_bank_rc_lines')} value={data.counts.lines} />
              <Stat label={t('aw_bank_state_matched')} value={data.counts.matched} tone={data.counts.matched ? '#166534' : undefined} />
              <Stat label={t('aw_bank_state_transfer')} value={data.counts.transfer} tone={data.counts.transfer ? '#1E40AF' : undefined} />
              <Stat label={t('aw_bank_state_ignored')} value={data.counts.ignored} />
              <Stat label={t('aw_bank_state_unmatched')} value={data.counts.unmatched} tone={data.counts.unmatched ? '#92400E' : undefined} />
            </div>
          </div>
          <div>
            <div style={sectionHead}>{t('aw_bank_rc_amounts')}{cur}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Stat label={t('aw_bank_sum_credits')} value={amt(data.amounts.credits)} />
              <Stat label={t('aw_bank_sum_debits')} value={amt(data.amounts.debits)} />
              <Stat label={t('aw_bank_sum_unmatched_credits')} value={amt(data.amounts.unmatchedCredits)} tone={Number(data.amounts.unmatchedCredits) ? '#92400E' : undefined} />
              <Stat label={t('aw_bank_sum_unmatched_debits')} value={amt(data.amounts.unmatchedDebits)} tone={Number(data.amounts.unmatchedDebits) ? '#92400E' : undefined} />
              {data.latest_statement
                ? <Stat label={t('aw_bank_rc_closing')} value={data.latest_statement.closing_balance === null || data.latest_statement.closing_balance === undefined ? '—' : amt(data.latest_statement.closing_balance)} sub={data.latest_statement.period_to ? `${t('aw_bank_rc_as_of')} ${dmy(data.latest_statement.period_to)}` : undefined} />
                : <Stat label={t('aw_bank_rc_closing')} value="—" sub={t('aw_bank_rc_no_statement')} />}
            </div>
          </div>
          <div>
            <div style={sectionHead}>{t('aw_bank_rc_months')}</div>
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              {!data.months.length ? <div style={emptyLine}>{t('aw_bank_no_lines')}</div> : (
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>{t('aw_bank_rc_month')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_sum_credits')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_sum_debits')}</th>
                      {/* `matched` per month counts every EXPLAINED line — matched, transfer and ignored — while
                          the card above counts 'matched' alone, so this column is headed accordingly. */}
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_rc_explained')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_state_unmatched')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.map(m => {
                      const [y, mo] = String(m.ym || '').split('-');
                      return (
                        <tr key={m.ym}>
                          <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{y && mo ? `${mo}/${y}` : m.ym}</td>
                          <td style={num}>{amt(m.credits)}</td>
                          <td style={num}>{amt(m.debits)}</td>
                          <td style={num}>{m.matched}</td>
                          <td style={{ ...num, color: m.unmatched ? '#92400E' : 'var(--text-tertiary)', fontWeight: m.unmatched ? 700 : 400 }}>{m.unmatched}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div style={note}>{t('aw_bank_rc_note')}</div>
        </>
      )}
    </div>
  );
}

// ── Transfers pane ──────────────────────────────────────────────────────────────────────────────
// A suggestion = a debit in one of the company's accounts and a credit in another, equal amount,
// within 3 days, both still unmatched (the server's rule, applied there). Confirming marks the two
// lines 'transfer' and pairs them; Undo returns both to unmatched. Nothing is posted anywhere.
export function TransfersPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AwBankTransfers | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let alive = true;
    api.awBankTransfers(ctx.clientId).then((d: AwBankTransfers) => { if (alive) { setData(d); setErr(''); } }).catch((e: any) => { if (alive) { setData({ suggestions: [], confirmed: [] }); setErr(readErr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Confirm / undo re-read through ctx.bump — the workspace's one signal, as in every writing pane.
  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(id); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(readErr(e, t)); } finally { setBusy(''); }
  };
  const confirmPair = (s: AwBankTransferSuggestion) => act(`${s.debit.id}:${s.credit.id}`, () => api.awBankTransferConfirm(ctx.clientId, s.debit.id, s.credit.id));
  const undo = (c: AwBankTransferConfirmed) => act(c.pair_id, () => api.awBankTransferUndo(ctx.clientId, c.pair_id));
  const sugg = data?.suggestions || [];
  const conf = data?.confirmed || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {err && <div style={errBox}>{err}</div>}
      <div>
        <div style={sectionHead}>{t('aw_bank_tr_suggestions')}</div>
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          {data === null ? <Loader /> : !sugg.length ? <div style={emptyLine}>{t('aw_bank_tr_no_suggestions')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>{t('aw_col_date')}</th>
                  <th style={th}>{t('aw_bank_tr_from')}</th>
                  <th style={th}>{t('aw_bank_tr_to')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_tr_days')}</th>
                  <th style={th}>{t('aw_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sugg.map(s => {
                  const key = `${s.debit.id}:${s.credit.id}`;
                  return (
                    <tr key={key}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        {dmy(s.debit.txn_date)}
                        {s.credit.txn_date !== s.debit.txn_date && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>→ {dmy(s.credit.txn_date)}</div>}
                      </td>
                      <td style={td}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.debit.account_number}</span><div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{s.debit.description}</div></td>
                      <td style={td}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.credit.account_number}</span><div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{s.credit.description}</div></td>
                      <td style={{ ...num, fontWeight: 700 }}>{amt(s.amount)}</td>
                      <td style={num}>{s.days}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <button onClick={() => confirmPair(s)} disabled={!!busy} style={{ ...btnPrimary, padding: '4px 10px', fontSize: 12 }}>
                          <i className={busy === key ? 'ti ti-loader' : 'ti ti-arrows-exchange'} aria-hidden /> {t('aw_bank_tr_confirm')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div>
        <div style={sectionHead}>{t('aw_bank_tr_confirmed')}</div>
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          {data === null ? <Loader /> : !conf.length ? <div style={emptyLine}>{t('aw_bank_tr_no_confirmed')}</div> : (
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>{t('aw_col_date')}</th>
                  <th style={th}>{t('aw_bank_tr_from')}</th>
                  <th style={th}>{t('aw_bank_tr_to')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th>
                  <th style={th}>{t('aw_bank_tr_by')}</th>
                  <th style={th}>{t('aw_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {conf.map(c => (
                  <tr key={c.pair_id}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(c.date)}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{c.from_account_number}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{c.to_account_number}</td>
                    <td style={{ ...num, fontWeight: 700 }}>{amt(c.amount)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.matched_by_name}{c.matched_at ? <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{fmtDateTime(c.matched_at)}</div> : null}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => undo(c)} disabled={!!busy} style={actBtn}><i className={busy === c.pair_id ? 'ti ti-loader' : 'ti ti-arrow-back-up'} aria-hidden /> {t('aw_bank_tr_undo')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div style={note}>{t('aw_bank_tr_note')}</div>
    </div>
  );
}
